import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Grid,
  SelectChangeEvent,
  FormHelperText,
  OutlinedInput,
  IconButton,
  ListSubheader,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FieldConfig } from './fieldsConfig';

interface ServiceFieldRendererProps {
  field: FieldConfig;
  value: any;
  onChange: (fieldName: string, value: any) => void;
  disabled?: boolean;
  error?: string;
  formData?: Record<string, any>;
}

const ServiceFieldRenderer: React.FC<ServiceFieldRendererProps> = ({
  field,
  value,
  onChange,
  disabled = false,
  error,
  formData = {}
}) => {

  const fieldRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  // Compute locked state from lockedBy config
  const { lockedValue, isLocked } = useMemo(() => {
    if (!field.lockedBy) return { lockedValue: null, isLocked: false };
    const sourceValue = formData[field.lockedBy.sourceField];
    if (sourceValue && field.lockedBy.valueMapping[sourceValue] !== undefined) {
      return { lockedValue: field.lockedBy.valueMapping[sourceValue], isLocked: true };
    }
    return { lockedValue: null, isLocked: false };
  }, [field.lockedBy, formData]);

  // Auto-sync locked value to parent form state
  useEffect(() => {
    if (isLocked && lockedValue !== null && value !== lockedValue) {
      onChange(field.name, lockedValue);
    }
  }, [isLocked, lockedValue, value, field.name, onChange]);

  const displayValue = isLocked ? lockedValue : value;
  const isDisabled = disabled || isLocked;


  useEffect(() => {
    if (error && fieldRef?.current) {
      fieldRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [error]);

  const handleSelectChange = (event: SelectChangeEvent<any>) => {
    onChange(field.name, event.target.value);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (field?.extraProps?.maxLength && event.target.value?.length > field?.extraProps?.maxLength) return
    onChange(field.name, event.target.value);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field.name, event.target.value);
  };

  const handleAgesChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(field.name, event.target.value);
  };

  const shouldShowField = () => {
    if (!field.conditional) return true;

    const { field: conditionalField, value: expectedValue } = field.conditional;
    const conditionalValue = formData[conditionalField];

    if (typeof expectedValue === 'function') {
      return expectedValue(conditionalValue);
    }

    // Check if conditionalValue is an array (from a multiselect)
    if (Array.isArray(conditionalValue)) {
      return conditionalValue.includes(expectedValue);
    }

    return conditionalValue === expectedValue;
  };
  // Shared style for inputs
  const commonInputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "11px",

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#ccc", // default border
        borderRadius: "11px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#f07fa9", // hover border
        borderRadius: "11px",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#f07fa9 !important", // focus border
        borderRadius: "11px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f07fa9", // label color on focus
    },
    "input::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
    "input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
    "input[type=number]": { MozAppearance: "textfield" },
  };

  if (!shouldShowField()) {
    return null;
  }

  return (
    <Grid item xs={12} sm={12} ref={fieldRef} sx={{ mb: 2 }}>
      {field.group && (
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
          {field.group}
        </Typography>
      )}
      <Typography className="field-label mb-2">
        {field.required && <span style={{ color: "red", marginRight: "4px" }}>*</span>}
        {field.label}
      </Typography>
      {
        field.name === "ages"
          ? <>
            <TextField
              type="text"
              label={field.label}
              value={displayValue || ""}
              onChange={handleAgesChange}
              fullWidth
              disabled={isDisabled}
              helperText={
                displayValue
                  ? error
                  : (formData?.kids
                    ? (formData.kids > 1
                      ? `Enter ${formData.kids} ages separated by commas (e.g., "3, 5.5, 7", "2 years, 4.5 years" or "10 days, 2 months")`
                      : `Enter age (e.g., "3", "3.5", "3 years", "18 months", or "10 days")`
                    )
                    : error
                  )
              }
              FormHelperTextProps={{
                error: !!(error && (displayValue || !formData?.kids)),
                sx: { fontSize: "15px", marginLeft: "4px" }
              }}
              multiline
              rows={2}
              sx={commonInputStyles}
              {...field?.extraProps}
            />
          </>
          : field.type === 'select'
            ? <>
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: "#f07fa9 !important", // label color when focused
                    },
                  }}
                >
                  {field.label}
                </InputLabel>

                <Select
                  multiple={field.multiple}
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  value={field.multiple ? (Array.isArray(displayValue) ? displayValue : []) : (displayValue || "")}
                  onChange={handleSelectChange}
                  disabled={isDisabled}
                  input={
                    <OutlinedInput
                      label={field.label}
                      sx={{
                        borderRadius: "11px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ccc", // default border
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#f07fa9", // hover border
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#f07fa9 !important", // focus border
                        },
                      }}
                    />
                  }
                  renderValue={field.multiple ? (selected: any) => (Array.isArray(selected) ? selected.join(', ') : selected) : undefined}
                  MenuProps={{
                    autoFocus: false,
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }}
                >
                  {field.multiple && (
                    <ListSubheader
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'background.paper',
                        pt: 1,
                        pb: 1,
                        borderBottom: '1px solid rgba(0,0,0,0.1)'
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {field.label}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(false);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListSubheader>
                  )}
                  {field.options?.map((option: any) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>

                {error && (
                  <FormHelperText error sx={{ fontSize: "15px", marginLeft: "4px" }}>
                    {error}
                  </FormHelperText>
                )}
              </FormControl>
              {!error && field.warning && (Array.isArray(displayValue) ? displayValue.some((v: any) => field.warning?.triggerValues.includes(v)) : field.warning.triggerValues.includes(displayValue)) && (
                <FormHelperText sx={{ fontSize: "13px", marginLeft: "4px", color: "#63605eff", marginTop: "4px" }}>
                  {field.warning.message}
                </FormHelperText>
              )}
            </>
            : field.type === "number"
              ? <>
                <TextField
                  type="number"
                  label={field.label}
                  value={displayValue || ""}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight" &&
                      e.key !== "Tab"
                    ) {
                      e.preventDefault();
                    }
                  }}
                  fullWidth
                  disabled={isDisabled}
                  error={false}
                  helperText={error}
                  FormHelperTextProps={{ error: !!error, sx: { fontSize: "15px", marginLeft: "4px" } }}
                  sx={commonInputStyles}
                  {...field?.extraProps}
                  inputProps={{
                    onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                    inputMode: "numeric",
                    maxLength: 11, // helps mobile keyboards but not desktop
                  }}
                />
                {!error && field.note && (
                  <FormHelperText
                    sx={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginLeft: "4px",
                      marginTop: "4px",
                    }}
                  >
                    {field.note}
                  </FormHelperText>
                )}
              </>
              : field.type === "date"
                ? <>
                  <TextField
                    type="date"
                    label={field.label}
                    value={displayValue || ""}
                    onChange={handleDateChange}
                    fullWidth
                    disabled={isDisabled}
                    error={false}
                    helperText={error}
                    FormHelperTextProps={{ error: !!error, sx: { fontSize: "15px", marginLeft: "4px" } }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={commonInputStyles}
                    {...field?.extraProps}
                  />
                </>
                : <>
                  <TextField
                    type="text"
                    label={field.label}
                    value={displayValue || ""}
                    onChange={handleInputChange}
                    fullWidth
                    disabled={isDisabled}
                    error={false}
                    helperText={error}
                    FormHelperTextProps={{ error: !!error, sx: { fontSize: "15px", marginLeft: "4px" } }}
                    sx={commonInputStyles}
                    {...field?.extraProps}
                  />
                </>
      }

    </Grid>
  )
};

export default ServiceFieldRenderer;
